import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import { LayoutDashboard, LogOut, Copy, Check, Smartphone, HelpCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { copyToClipboard } from '../../lib/clipboard';
import { toast } from 'sonner';
import NotificationBell from '../../components/NotificationBell';

const SCREENS = [
  { match: /^\/tm\/?$/, title: 'Dashboard', code: 'TM-P01' },
];
const getScreen = (pathname) => {
  for (const s of SCREENS) if (s.match.test(pathname)) return s;
  return { title: 'PriceCapture · UNO', code: '' };
};

const TMLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const screen = getScreen(location.pathname);
  const [copiedCode, setCopiedCode] = useState(false);

  const handleLogout = async () => { await logout(); navigate('/'); };
  const copyCode = async () => {
    if (!screen.code) return;
    const ok = await copyToClipboard(screen.code);
    if (ok) { setCopiedCode(true); toast.success(`${screen.code} copiado`); setTimeout(() => setCopiedCode(false), 1400); }
    else toast.error('No se pudo copiar');
  };

  const items = [
    { to: '/tm', icon: LayoutDashboard, label: 'Dashboard', end: true },
  ];

  return (
    <div className="min-h-screen flex bg-[#f8f6f1]">
      <aside className="w-60 shrink-0 border-r border-[#e8e5de] flex flex-col" data-testid="tm-sidebar">
        <Link to="/tm" className="px-5 py-5 border-b border-[#e8e5de] flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-md pc-logo-tile flex items-center justify-center shrink-0">
            <span className="text-white font-semibold text-[15px] leading-none tracking-tight">P</span>
          </div>
          <div className="min-w-0">
            <p className="font-medium text-[14px] tracking-tight leading-none">PriceCapture</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#8a8579] mt-1 truncate">TM · {user?.name || 'Territory'}</p>
          </div>
        </Link>
        <nav className="flex-1 p-3 space-y-1">
          {items.map(it => (
            <NavLink key={it.to} to={it.to} end={it.end} className={({isActive}) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] transition ${isActive ? 'bg-[#0f1d6f] text-white' : 'text-[#3a3a3a] hover:bg-[#e7eeff]'}`}>
              <it.icon size={15} strokeWidth={1.6} />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[#e8e5de]">
          <div className="px-2 mb-2">
            <p className="text-[12.5px] font-medium truncate">{user?.name}</p>
            <p className="text-[11px] text-[#8a8579] truncate">{user?.email}</p>
          </div>
          <button onClick={() => navigate('/app')} className="w-full flex items-center gap-2 px-3 py-2 text-[12.5px] text-[#5a5a5a] hover:bg-[#f3f0e9] rounded-lg mb-1" data-testid="tm-mobile-view">
            <Smartphone size={14}/> Vista móvil
          </button>
          <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 text-[12.5px] text-[#5a5a5a] hover:bg-[#f3f0e9] rounded-lg" data-testid="tm-logout">
            <LogOut size={14}/> Cerrar sesión
          </button>
        </div>
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 bg-[#f8f6f1]/85 backdrop-blur-md border-b border-[#e8e5de] px-8 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] tracking-[0.18em] uppercase text-[#8a8579] flex items-center gap-1.5">
              <span>Territory Manager</span>
              {screen.code && <span className="text-[#cfcabe]">·</span>}
              {screen.code && (
                <button type="button" onDoubleClick={copyCode} title={`Doble click para copiar ${screen.code}`} className="font-mono normal-case tracking-normal inline-flex items-center gap-1 px-1.5 py-0.5 -my-0.5 rounded hover:bg-[#1c1c1c]/5 transition group select-none" data-testid="tm-screen-code">
                  <span>{screen.code}</span>
                  {copiedCode ? <Check size={11} className="text-[#15803d]"/> : <Copy size={10.5} className="text-[#cfcabe] group-hover:text-[#8a8579] transition"/>}
                </button>
              )}
            </p>
            <h1 className="text-[15px] font-medium tracking-tight">{screen.title}</h1>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/manual-usuario.html#tm"
              target="_blank"
              rel="noreferrer"
              className="w-9 h-9 rounded-full hover:bg-[#1c1c1c]/5 flex items-center justify-center transition"
              title="Manual de uso"
              aria-label="Manual de uso"
              data-testid="tm-help-btn"
            >
              <HelpCircle size={17} className="text-[#3a3a3a]" />
            </a>
            <NotificationBell variant="light"/>
          </div>
        </header>
        <main className="flex-1 px-8 py-6 overflow-x-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default TMLayout;
